/**
 * Listing impression batch — what the client's impression buffer flushes
 * (via `sendBeacon`/`fetch keepalive`) to `POST /api/track/impressions`.
 *
 * Impressions are a SEPARATE event class from listing views
 * (`track-listing-view.ts`): a view = the buyer opened the listing; an
 * impression = a listing CARD was exposed while browsing (active snap-feed
 * card / search grid card ≥50% visible for ≥1s). 10–50× view volume, hence
 * the BATCHED wire shape (up to {@link MAX_IMPRESSION_EVENTS_PER_BATCH}
 * events per beacon) instead of one request per event.
 *
 * NO timestamps on the wire — `listing_impression_events.seen_at` is
 * server-side `now()`. The client buffer delays delivery by up to ~10s
 * (idle flush) which is irrelevant at the day granularity the rollup
 * operates on; carrying client clocks would only import their skew.
 *
 * `guestId`: same rationale and identity semantics as
 * `track-listing-view.ts` (see that file's header) — session-absent
 * fallback only, resolved server-side via `resolveViewerKey`; no claimed
 * `userId` is ever accepted on the wire.
 *
 * @see app/api/track/impressions/route.ts — handler
 * @see lib/analytics/impression-buffer.ts — canonical producer (the ONLY one)
 * @see supabase/migrations/20260714143719_listing_impression_events.sql — schema
 */

import { z } from "zod";
import { pinned } from "./pin";
import { TRACKING_CLIENTS, type TrackingClient } from "./tracking-client";

/**
 * Where the card was exposed. Single SoT for the variant dimension — the DB
 * CHECK constraint on `listing_impression_events.surface`, the buffer
 * intent, the observer hook and the route all reference these literals.
 * Launch surfaces only (O9): saved/profile/admin grids do NOT emit.
 *
 * - 'feed_snap'   — active card of a vertical snap feed (web feed, mobile
 *   "following" tab).
 * - 'search_grid' — search results grid card (≥50% visible for ≥1s; web +
 *   mobile).
 * - 'feed_grid'   — mobile home-feed 2-column grid (the default "live"
 *   tab). Grid browse ≠ snap ≠ search — honest new value per
 *   HANDOFF_MOBILE_TRACKING_2026_07_14 §2.4.
 */
export const IMPRESSION_SURFACES = ["feed_snap", "search_grid", "feed_grid"] as const;
export type ImpressionSurface = (typeof IMPRESSION_SURFACES)[number];

/** Hard cap per beacon — the buffer slices larger backlogs into multiple beacons. */
export const MAX_IMPRESSION_EVENTS_PER_BATCH = 50;

export interface ImpressionEvent {
  listingId: string;
  surface: ImpressionSurface;
  /** Card index at impression time (snap index / grid position). */
  position?: number;
}

export interface TrackImpressionsRequest {
  /** 1..50 events per beacon. */
  events: ImpressionEvent[];
  /** Guest identity fallback — see track-listing-view.ts header. Ignored when a session is present. */
  guestId?: string | null;
  /**
   * Which client produced the batch (one client per beacon, obviously).
   * OPTIONAL — the SERVER defaults omitted -> 'web'; mobile always sends
   * 'mobile'. See tracking-client.ts header.
   */
  client?: TrackingClient;
}

export const TrackImpressionsRequestSchema = pinned<TrackImpressionsRequest>()(
  z.object({
    events: z
      .array(
        z.object({
          listingId: z.string().min(1),
          surface: z.enum(IMPRESSION_SURFACES),
          position: z.number().int().min(0).optional(),
        }),
      )
      .min(1)
      .max(MAX_IMPRESSION_EVENTS_PER_BATCH),
    guestId: z.string().min(1).nullable().optional(),
    client: z.enum(TRACKING_CLIENTS).optional(),
  }),
);
